import { Button } from '@bennows/boows-react';
import { Check, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { actionSummary, actionTarget } from './action-text';
import type { AssistantAction } from './assistant-api';
import { NavButton } from './NavButton';

interface Props {
  readonly actions: readonly AssistantAction[];
  readonly overrides: Readonly<Record<string, AssistantAction>>;
  readonly conflicts: ReadonlySet<string>;
  readonly undoing: ReadonlySet<string>;
  readonly onUndo: (action: AssistantAction) => Promise<void>;
}

/**
 * What the assistant did in one answer (spec 110 section 4.15.6): one row per action with its state, Oeffnen
 * (the panel stays open) and Rueckgaengig. Every undo ends in a visible row state; there is no "undo all".
 */
export function ActionList({ actions, overrides, conflicts, undoing, onUndo }: Props) {
  const { t } = useTranslation();
  if (actions.length === 0) return null;
  return (
    <section className="ui-assistant-actions">
      <p className="ui-assistant-actions__title">
        <Check aria-hidden="true" />
        {t('assistant.actions.title', { count: actions.length })}
      </p>
      <ul className="ui-assistant-actions__list" aria-label={t('assistant.actions.list')}>
        {actions.map((original) => {
          const action = overrides[original.id] ?? original;
          const summary = actionSummary(t, action);
          const target = actionTarget(action);
          const conflict = conflicts.has(action.id);
          const busy = undoing.has(action.id);
          const canUndo = (action.state === 'done' || action.state === 'partially_undone') && !conflict;
          const state = conflict ? 'conflict' : action.state;
          return (
            <li key={action.id} className="ui-assistant-actions__row" tabIndex={-1}>
              <span className="ui-assistant-actions__text">
                <span className="ui-assistant-actions__summary">{summary}</span>
                <span role="status" className="ui-assistant-actions__status">
                  <span className="ui-assistant-actions__state">{t(`assistant.actions.state.${state}`)}</span>
                  {action.state === 'partially_undone' && action.failed_parts.length > 0 ? (
                    <span className="ui-assistant-actions__state">
                      {t('assistant.actions.partsFailed', { parts: action.failed_parts.map((part) => t(`assistant.actions.part.${part}`, { defaultValue: part })).join(', ') })}
                    </span>
                  ) : null}
                </span>
              </span>
              {target ? (
                <NavButton to={target} label={t('assistant.actions.openLabel', { summary })}>
                  {t('assistant.actions.open')}
                </NavButton>
              ) : null}
              {canUndo ? (
                <Button
                  variant="secondary-outline"
                  size="sm"
                  iconPrefix={<RotateCcw aria-hidden="true" />}
                  loading={busy}
                  aria-label={t(action.state === 'partially_undone' ? 'assistant.actions.undoRestLabel' : 'assistant.actions.undoLabel', { summary })}
                  onClick={async (event) => {
                    // The pressed button may leave the document; the row takes the focus so it is not lost.
                    const row = event.currentTarget.closest('li');
                    await onUndo(action);
                    row?.focus();
                  }}
                >
                  {t(action.state === 'partially_undone' ? 'assistant.actions.undoRest' : 'assistant.actions.undo')}
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
